import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { TrialGroupItemTemplateCategoriesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsTrialGroupSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupItemTemplateCategoriesActions from "../../../actions/trialGroupItemTemplateCategories";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import TrialGroupLookupsLayout from "../TrialGroupLookupsLayout/TrialGroupLookupsLayout";

import "./TrialGroupItemTemplateCategoriesPage.scss";

interface TrialGroupItemTemplateCategoriesPageProps {
    formFilter: IFormFilter;
    trialItemTemplateCategorys: Dtos.TrialGroupItemTemplateCategory[];
    loadingTrialGroupItemTemplateCategorys: boolean;
    loadTrialGroupItemTemplateCategorysSuccess: boolean;
    loadTrialGroupItemTemplateCategorysFailure: boolean;
    loadTrialGroupItemTemplateCategorysErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialGroupItemTemplateCategorys: TrialGroupItemTemplateCategoriesActions.ITrialGroupItemTemplateCategoryClearActionCreator;
    loadTrialGroupItemTemplateCategorys: TrialGroupItemTemplateCategoriesActions.ITrialGroupItemTemplateCategoryLoadActionCreator;
    filter: TrialGroupItemTemplateCategoriesActions.ITrialGroupItemTemplateCategoryFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialGroupItemTemplateCategoriesPage: FC<
    TrialGroupItemTemplateCategoriesPageProps
> = ({
    formFilter,
    trialItemTemplateCategorys,
    loadingTrialGroupItemTemplateCategorys,
    loadTrialGroupItemTemplateCategorysSuccess,
    loadTrialGroupItemTemplateCategorysFailure,
    loadTrialGroupItemTemplateCategorysErrorCode,
    permissions,
    clearTrialGroupItemTemplateCategorys,
    loadTrialGroupItemTemplateCategorys,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showTrialGroupItemTemplateCategorys =
        !loadingTrialGroupItemTemplateCategorys &&
        loadTrialGroupItemTemplateCategorysSuccess;


    // Load trial group item template categories
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsTrialGroupSubMenu(permissions));
            setBreadcrumbs(TrialGroupItemTemplateCategoriesBreadcrumbs());

            loadTrialGroupItemTemplateCategorys();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsTrialGroupSubMenu,
        permissions,
        setBreadcrumbs,
        TrialGroupItemTemplateCategoriesBreadcrumbs,
        loadTrialGroupItemTemplateCategorys,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupItemTemplateCategorysFailure) {
            navigate(`/error/${loadTrialGroupItemTemplateCategorysErrorCode}`);
        }
    }, [
        loadTrialGroupItemTemplateCategorysFailure,
        loadTrialGroupItemTemplateCategorysErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the trial group item template categories on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupItemTemplateCategorys();
        };
    }, [clearTrialGroupItemTemplateCategorys]);

    const createTable = useMemo(() => {
        if (
            !trialItemTemplateCategorys ||
            trialItemTemplateCategorys.length == 0
        ) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no item trial group item
                            template categories to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination =
            trialItemTemplateCategorys.length > DEFAULT_PAGE_SIZE;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={trialItemTemplateCategorys}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? DEFAULT_PAGE_SIZE
                            : trialItemTemplateCategorys.length
                    }
                    pageSize={
                        showPagination
                            ? undefined
                            : trialItemTemplateCategorys.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no item trial group item template categories to display"
                    resizable={false}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/settings/trialgroup/itemtemplatecategories/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                            style: {
                                color:
                                    rowInfo &&
                                    rowInfo.original &&
                                    rowInfo.original.active
                                        ? "#212529"
                                        : "#9e9e9e",
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        trialItemTemplateCategorys,
        DEFAULT_PAGE_SIZE,
        PAGE_SIZE_OPTIONS,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Trial Group Item Categories</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.CategoriesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/trialgroup/itemtemplatecategories/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showTrialGroupItemTemplateCategorys ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialGroupItemTemplateCategorys ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showTrialGroupItemTemplateCategorys,
        createTable,
    ]);

    return (
        <TrialGroupLookupsLayout
            loading={loadingTrialGroupItemTemplateCategorys}
            permission={Dtos.Permission.TrialGroupItemTemplateCategoriesView}
        >
            {renderContent}
        </TrialGroupLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupItemTemplateCategoriesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    return {
        formFilter: state.trialGroupItemTemplateCategory.formFilter,
        trialItemTemplateCategorys:
            state.trialGroupItemTemplateCategory.formFilteredData,
        loadingTrialGroupItemTemplateCategorys:
            state.trialGroupItemTemplateCategory.loadState &&
            state.trialGroupItemTemplateCategory.loadState.status ==
                RequestState.Pending,
        loadTrialGroupItemTemplateCategorysSuccess:
            state.trialGroupItemTemplateCategory.loadState &&
            state.trialGroupItemTemplateCategory.loadState.status ==
                RequestState.Success,
        loadTrialGroupItemTemplateCategorysFailure:
            state.trialGroupItemTemplateCategory.loadState &&
            state.trialGroupItemTemplateCategory.loadState.status ==
                RequestState.Failure,
        loadTrialGroupItemTemplateCategorysErrorCode:
            state.trialGroupItemTemplateCategory.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupItemTemplateCategoriesPageProps> => {
    return {
        loadTrialGroupItemTemplateCategorys: bindActionCreators(
            TrialGroupItemTemplateCategoriesActions.LoadTrialGroupItemTemplateCategory,
            dispatch
        ),
        clearTrialGroupItemTemplateCategorys: bindActionCreators(
            TrialGroupItemTemplateCategoriesActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            TrialGroupItemTemplateCategoriesActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupItemTemplateCategoriesPage);
